import orderStyles from 'styles/order.module.css';
import {addPeriodToString, capitalize, smoothScrollToTop} from '@crm/utils/helpers';
import React, {useEffect, useState} from 'react';
import {housekeeprApi} from '@crm/api/housekeepr-api';
import AddressField from '@crm/components/address-look-up/address-field';
import {useAppDispatch, useAppSelector} from '@crm/services/store';
import {
    selectWindowCleaningAddress,
    selectWindowCleaningMaxPage,
    selectWindowCleaningResidenceType,
    selectWindowCleaningSecondaryGlazing,
    selectWindowCleaningServiceId,
    selectWindowCleaningServiceTypeId,
} from '@crm/services/window-cleaning-selectors';
import {windowCleaningActions, WindowCleaningPage} from '@crm/services/window-cleaning-slice';
import {skipToken} from '@reduxjs/toolkit/query';
import {InvalidAddressDialog} from '@crm/components/layout/landing-page/order-wizard/steps/address-lookup/invalid-address-dialog';
import {authSelectors} from '@crm/services/auth-slice';

type WindowCleaningHomeCardProps = {hidden?: boolean};

export const WindowCleaningHomeCard = (props: WindowCleaningHomeCardProps) => {
    const dispatch = useAppDispatch();

    const maxPage = useAppSelector(selectWindowCleaningMaxPage);

    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

    const authenticated = useAppSelector(authSelectors.selectAuthenticated);
    const serviceTypeId = useAppSelector(selectWindowCleaningServiceTypeId);
    const selectedAddress = useAppSelector(selectWindowCleaningAddress);
    const selectedResidenceType = useAppSelector(selectWindowCleaningResidenceType);
    const selectedServiceId = useAppSelector(selectWindowCleaningServiceId);
    const hasSecondaryGlazing = useAppSelector(selectWindowCleaningSecondaryGlazing);

    const {data: serviceTypesResult} = housekeeprApi.useGetServiceTypesQuery();

    const {data: addressResult, error: addressError} = housekeeprApi.useRegisterAddressQuery(
        selectedAddress
            ? {
                  darId: selectedAddress.adresse.id,
              }
            : skipToken
    );

    const {data: filteredServicesResult} = housekeeprApi.useGetServicesWithoutPriceQuery(
        serviceTypeId && addressResult
            ? {
                  serviceTypeId,
                  parish: addressResult.parish,
              }
            : skipToken
    );

    const {data: servicesResult} = housekeeprApi.useGetServicesWithoutFilterQuery(
        serviceTypeId && !filteredServicesResult ? serviceTypeId : skipToken
    );

    const services = filteredServicesResult ?? servicesResult;
    const service = services?.find((service) => service.id == selectedServiceId);

    const {data: residenceTypesResult} = housekeeprApi.useGetResidenceTypesQuery();

    const serviceType = serviceTypeId
        ? serviceTypesResult?.find((serviceType) => serviceType.id == serviceTypeId)
        : serviceTypesResult?.find((serviceType) => serviceType.type == 'window_cleaning');

    useEffect(() => {
        if (!serviceTypeId && serviceType) {
            dispatch(windowCleaningActions.start({serviceTypeId: serviceType.id, authenticated}));
        }
    }, [serviceTypeId, serviceType, dispatch, authenticated]);

    useEffect(() => {
        if (
            selectedServiceId &&
            filteredServicesResult &&
            !filteredServicesResult.some((service) => service.id == selectedServiceId)
        ) {
            dispatch(windowCleaningActions.clearService());
        }
    }, [serviceTypeId, serviceType, dispatch, selectedServiceId, filteredServicesResult]);

    useEffect(() => {
        if (!addressResult && !!addressError && selectedServiceId) {
            dispatch(windowCleaningActions.clearService());
        }
    }, [addressResult, addressError, selectedAddress, dispatch, selectedServiceId]);

    return (
        <div className={props.hidden ? orderStyles.hidden : orderStyles.cardWrapper}>
            <div className={orderStyles.card}>
                <div className={orderStyles.orderSection}>
                    <h1 className={orderStyles.cardH1}>Hvad er din adresse?</h1>
                    <div id={'address-content'} className={`${orderStyles.orderSectionContent}`}>
                        <div
                            className={`${orderStyles.addressCard} cursor-pointer`}
                            onClick={() => setIsAddressModalOpen(true)}>
                            {selectedAddress ? <p>{selectedAddress.tekst}</p> : <p>Indtast din adresse</p>}
                        </div>
                    </div>
                    {((services && services.length == 0) || !!addressError) && (
                        <InvalidAddressDialog
                            mobile={true}
                            lightBlue={true}
                            hideCloseButton={true}
                            text={
                                addressError
                                    ? (addressError as {status?: number}).status == 404 &&
                                      !!(
                                          addressError as {
                                              data?: any;
                                          }
                                      ).data?.error
                                        ? `${addPeriodToString(
                                              (addressError as any).data.error
                                          )} Dobbelttjek adressen eller kontakt os.`
                                        : 'Vi har problemer med at finde din adresse. Prøv venligst igen senere eller kontakt os.'
                                    : 'Det ser desværre ud til, at vi ikke tilbyder vinduespudsning på denne addresse. Tjek at du har indtastet den rigtige adresse, prøv igen senere eller kontakt os.'
                            }
                        />
                    )}
                </div>
                {!services ||
                    (services.length > 0 && !addressError && (
                        <>
                            <div className={orderStyles.orderSection}>
                                <h1 className={`${orderStyles.cardH1}`}>Hvordan bor du?</h1>
                                <div
                                    id={'residence-type-content'}
                                    className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap}`}>
                                    {residenceTypesResult?.map((residenceType) => (
                                        <div
                                            className={`${orderStyles.cardOption} ${
                                                selectedResidenceType == residenceType.type ? orderStyles.selected : ''
                                            }`}
                                            key={residenceType.type}
                                            onClick={() =>
                                                dispatch(windowCleaningActions.setResidenceType(residenceType.type))
                                            }>
                                            {residenceType.description}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={orderStyles.orderSection}>
                                <h1 className={`${orderStyles.cardH1}`}>Hvilke sider af vinduerne skal tages?</h1>
                                <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                                    Der skal være adgang til vinduerne, så hvis du vælger pudsning inde skal du være
                                    hjemme. Udendørs bruger vi rentvandsanlæg op til 3. sal. Bor du højere kan vi kun
                                    pudse ude, hvis vi har adgang inde fra lejligheden.
                                </p>
                                <div
                                    id={'service-content'}
                                    className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap}`}>
                                    {services?.map((service) => {
                                        return (
                                            <div
                                                className={`${orderStyles.cardOption} ${
                                                    selectedServiceId == service.id ? orderStyles.selected : ''
                                                }`}
                                                onClick={() => {
                                                    dispatch(windowCleaningActions.setService(service));
                                                    if (
                                                        !service.name
                                                            .toLowerCase()
                                                            .replace('vinduespudsning', '')
                                                            .includes('ind')
                                                    ) {
                                                        dispatch(windowCleaningActions.setSecondaryGlazing(false));
                                                    }
                                                }}
                                                key={service.id}
                                                id={`service-${service.id}`}>
                                                {capitalize(
                                                    service.name.replace('Vinduespudsning ', '').replace('/', ' og ')
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {service?.name.toLowerCase().replace('vinduespudsning', '').includes('ind') && (
                                <div className={orderStyles.orderSection}>
                                    <h1 className={`${orderStyles.cardH1}`}>Har dine vinduer forsats?</h1>
                                    <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                                        Vinduer med to lag, der åbnes individuelt, har forsats.
                                    </p>
                                    <div
                                        id={'glazing-content'}
                                        className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap}`}>
                                        <div
                                            onClick={() => dispatch(windowCleaningActions.setSecondaryGlazing(true))}
                                            className={`${orderStyles.cardOption} ${
                                                hasSecondaryGlazing ? orderStyles.selected : ''
                                            }`}>
                                            Ja
                                        </div>
                                        <div
                                            onClick={() => dispatch(windowCleaningActions.setSecondaryGlazing(false))}
                                            className={`${orderStyles.cardOption} ${
                                                !hasSecondaryGlazing ? orderStyles.selected : ''
                                            }`}>
                                            Nej
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ))}
                {isAddressModalOpen && (
                    <div className={`${orderStyles.modalContainer}`}>
                        <div className={`${orderStyles.modalCard}`}>
                            <h1 className={orderStyles.cardH1}>Indtast og vælg din adresse</h1>
                            <div className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                                <AddressField
                                    label='Adresse'
                                    name={'address'}
                                    onClick={(address) => {
                                        dispatch(windowCleaningActions.setAddress(address));
                                        setIsAddressModalOpen(false);
                                    }}
                                    hideOptions={false}
                                    hideArrow={true}
                                />
                            </div>
                            <div className={orderStyles.closeButton} onClick={() => setIsAddressModalOpen(false)}>
                                <svg
                                    id='Component_5_1'
                                    data-name='Component 5 – 1'
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='100%'
                                    height='100%'
                                    viewBox='0 0 22.715 22.715'>
                                    <path
                                        id='np_menu_1591047_000000'
                                        d='M11.821,26.763A1.682,1.682,0,0,0,10,28.256a1.682,1.682,0,0,0,1.821,1.493h25.5a1.682,1.682,0,0,0,1.821-1.493,1.682,1.682,0,0,0-1.821-1.493Z'
                                        transform='translate(13.964 -25.995) rotate(45)'
                                    />
                                    <path
                                        id='np_menu_1591047_000000-2'
                                        data-name='np_menu_1591047_000000'
                                        d='M1.821,2.986A1.682,1.682,0,0,1,0,1.493,1.682,1.682,0,0,1,1.821,0h25.5a1.682,1.682,0,0,1,1.821,1.493,1.682,1.682,0,0,1-1.821,1.493Z'
                                        transform='translate(22.715 2.111) rotate(135)'
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div
                className={`${orderStyles.buttonWrapper} ${orderStyles.outline} ${
                    maxPage < WindowCleaningPage.WINDOWS ? orderStyles.disabled : ''
                } self-end w-[200px]`}>
                <button
                    onClick={() => {
                        dispatch(windowCleaningActions.setPage(WindowCleaningPage.WINDOWS));
                        smoothScrollToTop();
                    }}>
                    Videre
                </button>
            </div>
        </div>
    );
};

export default WindowCleaningHomeCard;
